import React, { useEffect, useRef } from 'react';
import { useLocale } from '../../../locales';

interface ImagePreviewModalProps {
  imageUrl: string;
  imageName?: string;
  onClose: () => void;
}

const ImagePreviewModal: React.FC<ImagePreviewModalProps> = ({
  imageUrl,
  imageName,
  onClose,
}) => {
  const { t } = useLocale();
  const contentRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    contentRef.current?.focus();
  }, []);

  useEffect(() => {
    const listener = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };

    window.addEventListener('keydown', listener);
    return () => window.removeEventListener('keydown', listener);
  }, [onClose]);

  return (
    <div className="qe-modal" role="presentation">
      <div className="qe-modal__backdrop" onClick={onClose} />
      <div className="qe-modal__content qe-modal__content--image-preview" ref={contentRef} tabIndex={-1}>
        <div className="qe-modal__header">
          <div className="qe-modal__header-content">
            <h2 className="qe-modal__title">{t("imagePreview.title")}</h2>
            {imageName && (
              <p className="qe-modal__subtitle">{imageName}</p>
            )}
          </div>
          <button
            type="button"
            className="qe-button qe-button--ghost qe-button--icon"
            onClick={onClose}
            aria-label={t("imagePreview.close")}
          >
            ✕
          </button>
        </div>

        <div className="qe-modal__body qe-modal__body--image-preview">
          <div className="qe-image-preview__container">
            <img
              src={imageUrl}
              alt={imageName || t("imagePreview.altFallback")}
              className="qe-image-preview__image"
              onError={(e) => {
                const target = e.target as HTMLImageElement;
                target.style.display = 'none';
                const container = target.parentElement;
                if (container) {
                  const p = document.createElement('p');
                  p.style.cssText = 'color: var(--qe-text-muted); text-align: center; padding: 40px;';
                  p.textContent = t("imagePreview.loadError");
                  container.appendChild(p);
                }
              }}
            />
          </div>
        </div>

        <div className="qe-modal__footer">
          <button
            type="button"
            className="qe-button qe-button--secondary"
            onClick={onClose}
          >
            {t("imagePreview.close")}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ImagePreviewModal;

